import { FC, Fragment, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  courseHasUnsavedStationChangeAtom,
  CourseRouteTypeEnum,
  DirectionEnum,
  ICourseStationInfo,
  resolveCourseTemplateRideEndpointsList,
} from "@ryde/shared";

import {
  CourseDirectionTemplateBannerStyled,
  CourseEditTopInfoContainerStyled,
} from "./course.styled";
import { useCourseEditTopInfo } from "./use-course-edit-top-info";

export const CourseEditTopInfoContainer: FC = () => {
  const { t } = useTranslation();
  const { rebuildTypeBannerText } = useCourseEditTopInfo();
  const courseHasUnsavedStationChange = useRecoilValue(
    courseHasUnsavedStationChangeAtom,
  );
  const watchedCourseDirection = useWatch({ name: "metadata.direction" });
  const routeType = useWatch({ name: "routeType" }) as
    CourseRouteTypeEnum | undefined;
  const stationsInfo = useWatch({ name: "metadata.stationsInfo" }) as
    ICourseStationInfo[] | undefined;
  const isBDirectionTemplate = watchedCourseDirection === DirectionEnum.DropOff;

  const bannerLabel = useMemo(() => {
    const routeTypeLabel = routeType
      ? t(`selectListLabel.${routeType}`)
      : t("selectListLabel.Shuttle");
    const directionLabel = isBDirectionTemplate
      ? t("selectListLabel.DirB")
      : t("selectListLabel.DirA");

    return t("course.COURSE_DIRECTION_TEMPLATE_BANNER", {
      routeType: routeTypeLabel,
      count: stationsInfo?.length ?? 0,
      direction: directionLabel,
    });
  }, [isBDirectionTemplate, routeType, stationsInfo?.length, t]);

  const rideEndpointsContent = useMemo(() => {
    const rides = resolveCourseTemplateRideEndpointsList({
      stationsInfo,
      courseDirectionTemplate: watchedCourseDirection,
    });

    if (!rides?.length) {
      return undefined;
    }

    return rides.map((ride, index) => {
      const directionLabel =
        ride.direction === DirectionEnum.PickUp
          ? t("selectListLabel.DirA")
          : t("selectListLabel.DirB");

      return (
        <Fragment key={ride.direction}>
          {index > 0 && ", "}
          {t("course.DIRECTION_TEMPLATE_RIDE_LEAD", {
            direction: directionLabel,
          })}
          <span className="direction-template-station-name">
            {ride.originStationName}
          </span>
          {t("course.DIRECTION_TEMPLATE_RIDE_TO")}
          <span className="direction-template-station-name">
            {ride.destinationStationName}
          </span>
        </Fragment>
      );
    });
  }, [stationsInfo, t, watchedCourseDirection]);

  return (
    <CourseEditTopInfoContainerStyled>
      <CourseDirectionTemplateBannerStyled
        data-e2e="course-direction-template-banner"
        $isBDirectionTemplate={isBDirectionTemplate}
      >
        <p className="direction-template-label">{bannerLabel}</p>
        {rideEndpointsContent && (
          <p
            className="direction-template-availability-note"
            data-e2e="course-direction-template-rides-note"
          >
            {rideEndpointsContent}
          </p>
        )}
      </CourseDirectionTemplateBannerStyled>

      {courseHasUnsavedStationChange && (
        <div className="info_item">
          <p>* {t(`course.ADDITION_OR_REMOVAL_OF_STATIONS_DETECTED`)}</p>
        </div>
      )}
      {rebuildTypeBannerText && (
        <div className="info_item">
          <p>* {rebuildTypeBannerText}</p>
        </div>
      )}
    </CourseEditTopInfoContainerStyled>
  );
};
